import {
  type ReactElement,
  type ReactNode,
  cloneElement,
  isValidElement,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import {
  Badge,
  Button,
  Separator,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
  cn,
} from '@gw/ui';
import {
  AppWindow,
  Briefcase,
  Building2,
  ChevronLeft,
  ChevronRight,
  Database,
  Home,
  LayoutDashboard,
  Settings,
  Store,
  Target,
  X,
} from 'lucide-react';
import { useLocation } from 'react-router-dom';

import { PrefetchLink } from '@/components/navigation/PrefetchLink';
import { useFeatureFlag } from '@/hooks/useFeatureFlag';
import { useAuthStore, useOrganizationStore, usePublisherAgreementStore } from '@/stores/store';

interface SidebarProps {
  isOpen: boolean;
  isCollapsed: boolean;
  onClose: () => void;
  onToggleCollapse: () => void;
}

interface NavigationItem {
  title: string;
  href: string;
  icon: React.ComponentType<{ className?: string }>;
  requiredRole?: string;
  requiredRoles?: readonly string[]; // Any of these roles grants access
  requiredFeatureFlag?: string; // GW-1284: Feature flag that must be enabled
}

// GW-8104: Home — standalone, ungrouped, top of sidebar (target IA §1.2)
const homeNavigationItem: NavigationItem = {
  title: 'Home',
  href: '/',
  icon: Home,
};

// GW-8104: Marketplace group (target IA §1.2) — Missions/Datasets/Services/
// Applications link to their real browse pages (GW-5553 mission catalog,
// GW-3070 service browse, dataset browse, GW-8281/8282 application browse),
// NOT the `?type=`/`?cat=` Home shortcuts the wireframe/outline originally
// specified — SearchNextPage's own MarketplaceCategoryCard tiles already
// navigate to these exact routes (see SearchNextPage.tsx), and the routes
// are real SEO-indexable browse pages, not just Home filters.
const marketplaceNavigationItems: NavigationItem[] = [
  {
    // GW-5553: Missions browse entry, gated by missions_marketplace feature flag
    title: 'Missions',
    href: '/marketplace/missions',
    icon: Target,
    requiredFeatureFlag: 'missions_marketplace',
  },
  {
    // GW-2149: Datasets link gated behind datasets_marketplace feature flag
    title: 'Datasets',
    href: '/marketplace/datasets',
    icon: Database,
    requiredFeatureFlag: 'datasets_marketplace',
  },
  {
    // GW-8665: Services link gated behind services_marketplace feature flag
    title: 'Services',
    href: '/marketplace/services',
    icon: Briefcase,
    requiredFeatureFlag: 'services_marketplace',
  },
  {
    // GW-8662: Applications browse entry, gated by applications_marketplace
    // feature flag (GW-8281/8282 restored the route/page; only this sidebar
    // link was missing).
    title: 'Applications',
    href: '/marketplace/applications',
    icon: AppWindow,
    requiredFeatureFlag: 'applications_marketplace',
  },
];

// GW-8104: Workspace group (target IA §1.2) — single "My Work" item.
// GW-12256: the link now points at the bare `/personal` section root.
// PersonalIndexRedirect (PersonalRedirect.tsx) owns the flag-aware default-tab
// choice (missions when missions_marketplace is enabled, sessions otherwise),
// so the sidebar no longer needs to replicate that fallback — pointing at the
// section root mirrors the /organization and /publisher sidebar entries.
const WORKSPACE_MY_WORK_ITEM: NavigationItem = {
  title: 'My Work',
  href: '/personal',
  icon: LayoutDashboard,
};

// GW-8104: Settings — standalone, ungrouped, bottom of sidebar (target IA
// §1.2), linking to the real settings landing route (GW-8051).
// GW-8263: /settings is the canonical route (flipped from /personal/settings).
const settingsNavigationItem: NavigationItem = {
  title: 'Settings',
  href: '/settings',
  icon: Settings,
};

// GW-430: External help link URL (opens in new tab) — GW-8104: Sidebar's own
// copy of this link was removed; the Header's UserMenu (HeaderHost.tsx) already
// surfaces an identical "Help" item, so this sidebar constant and its markup
// are no longer needed here.

// GW-1433/GW-8265: Discord support chat link for publisher-role users moved
// out of the sidebar entirely and now renders at the top of every
// /publisher/* page (see PublisherWorkspacePage.tsx) — there is no
// sidebar-local equivalent left to maintain here.

// Organization — single link into the shared /organization/:tab TabbedDetailPage
// shell (OrganizationWorkspacePage). Individual former sub-items (Summary,
// Members & Roles, SMART Tokens, Transactions, Analytics, Service Roster,
// RFIs, Terms of Service) are now tabs inside that page, not separate sidebar
// links — see docs/target-ia-outline.md §1.2.
const organizationNavigationItems: NavigationItem[] = [
  {
    title: 'Organization',
    // GW-12256: link to the section root; /organization redirects to
    // /organization/summary (organization.routes.tsx), so the destination is
    // unchanged but the sidebar now points at the canonical section entry.
    href: '/organization',
    icon: Building2,
    requiredRole: 'org_admin' as const,
  },
];

// Publishing — single link into the shared /publisher/:tab TabbedDetailPage
// shell (PublisherWorkspacePage). Individual former sub-items (Summary, API
// Key, Applications, Datasets, Services) are now tabs inside that page, not
// separate sidebar links — see docs/target-ia-outline.md §1.2.
const developerNavigationItems: NavigationItem[] = [
  {
    title: 'Publishing',
    // GW-12256: link to the section root; /publisher redirects to
    // /publisher/summary (publisher.routes.tsx), so the destination is
    // unchanged but the sidebar now points at the canonical section entry.
    href: '/publisher',
    icon: Store,
    requiredRoles: [
      'app_admin',
      'org_admin',
      'dataset_admin',
      'service_admin',
      'consultant',
    ] as const,
  },
];

// GW-12209: A nav entry's section is "active" for its own root path AND any
// child route beneath it (tab sub-routes like /settings/security, and catalog
// item detail pages like /marketplace/datasets/{slug}), not only an exact
// pathname match. This mirrors the Admin group's existing startsWith behavior.
// The Home entry deliberately does NOT use this — it stays an EXACT `===` match
// (see its isActive below) so `/` does not light up on every route.
function isSectionActive(pathname: string, section: string): boolean {
  return pathname === section || pathname.startsWith(`${section}/`);
}

// GW-12209/GW-12256: My Work links to the /personal section root, but the
// section it represents spans ALL /personal/* tabs (queries, files, rfis,
// sessions, notifications, …). Match against the section root so every tab
// keeps My Work highlighted.
const WORKSPACE_SECTION = '/personal';

/**
 * Wraps children with a shadcn Tooltip when the sidebar is collapsed.
 * Shows the tooltip on the right side of the sidebar.
 * Accepts an optional `description` for secondary tooltip text (e.g. "N TOS to Review").
 */
function SidebarTooltip({
  children,
  label,
  description,
  isCollapsed,
}: {
  children: ReactNode;
  label: string;
  description?: string;
  isCollapsed: boolean;
}) {
  if (!isCollapsed) return children;

  // Add aria-label for accessibility when only the icon is visible
  const accessibleChild = isValidElement(children)
    ? cloneElement(children as ReactElement<React.HTMLAttributes<HTMLElement>>, {
        'aria-label': label,
      })
    : children;

  return (
    <Tooltip>
      <TooltipTrigger asChild>{accessibleChild}</TooltipTrigger>
      <TooltipContent side="right">
        <div>{label}</div>
        {description && <div className="text-muted-foreground text-xs">{description}</div>}
      </TooltipContent>
    </Tooltip>
  );
}

/**
 * Renders a sidebar section header with a tooltip when collapsed.
 * When `href` is provided, the label is rendered as a navigable PrefetchLink.
 */
function SectionHeader({
  label,
  isCollapsed,
  href,
}: {
  label: string;
  isCollapsed: boolean;
  href?: string;
}) {
  if (isCollapsed) {
    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <div className="flex cursor-default justify-center px-3 py-2">
            <span className="text-muted-foreground/40 text-[10px] font-bold tracking-widest uppercase">
              {label.charAt(0)}
            </span>
          </div>
        </TooltipTrigger>
        <TooltipContent side="right">{label}</TooltipContent>
      </Tooltip>
    );
  }

  if (href) {
    return (
      <div className="px-3 py-2">
        <PrefetchLink
          to={href}
          className="text-muted-foreground hover:text-foreground text-xs font-semibold tracking-wider uppercase transition-colors"
        >
          {label}
        </PrefetchLink>
      </div>
    );
  }

  return (
    <div className="px-3 py-2">
      <h4 className="text-muted-foreground text-xs font-semibold tracking-wider uppercase">
        {label}
      </h4>
    </div>
  );
}

/**
 * Sidebar - Side navigation for protected pages
 */
export function Sidebar({ isOpen, isCollapsed, onClose, onToggleCollapse }: SidebarProps) {
  const location = useLocation();
  const { user } = useAuthStore();
  const { currentOrg } = useOrganizationStore();
  const isGlobalAdmin = user?.role === 'global_admin';

  // GW-2351: Publisher agreement re-acceptance badge on Terms of Service nav item
  const { publisherAgreementNeedsReacceptance } = usePublisherAgreementStore();

  // Show glow animation on mount to highlight the collapse button
  const [showGlow, setShowGlow] = useState(true);

  // GW-4687: Only enable sidebar width/transform transition during intentional
  // collapse/expand interactions. Always-on transitions cause visible stutter when
  // React re-renders cascade from store subscriptions during search.
  const [isTransitioning, setIsTransitioning] = useState(false);
  const prevIsOpenRef = useRef(isOpen);
  useEffect(() => {
    if (prevIsOpenRef.current !== isOpen) {
      setIsTransitioning(true);
      prevIsOpenRef.current = isOpen;
    }
  }, [isOpen]);

  useEffect(() => {
    // Remove glow after 3 seconds
    const timer = setTimeout(() => {
      setShowGlow(false);
    }, 3000);

    return () => clearTimeout(timer);
  }, []);

  // GW-1991: Focus trapping for mobile sidebar
  useEffect(() => {
    if (!isOpen || typeof window === 'undefined' || window.innerWidth >= 1024) return;

    const sidebar = document.querySelector('aside[role="dialog"]') as HTMLElement;
    if (!sidebar) return;

    const previouslyFocused = document.activeElement as HTMLElement;

    const focusableElements = sidebar.querySelectorAll<HTMLElement>(
      'a[href], button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])'
    );
    const firstFocusable = focusableElements[0];
    const lastFocusable = focusableElements[focusableElements.length - 1];

    firstFocusable?.focus();

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
        return;
      }
      if (e.key !== 'Tab') return;

      if (e.shiftKey) {
        if (document.activeElement === firstFocusable) {
          e.preventDefault();
          lastFocusable?.focus();
        }
      } else {
        if (document.activeElement === lastFocusable) {
          e.preventDefault();
          firstFocusable?.focus();
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      previouslyFocused?.focus();
    };
  }, [isOpen, onClose]);

  // GW-1284: Check feature flags for marketplace items
  const datasetsFeatureFlag = useFeatureFlag('datasets_marketplace');
  // GW-1578: Check feature flag for gift cards
  const giftCardsFeatureFlag = useFeatureFlag('gift_cards_enabled');
  // GW-1851: Check feature flag for referral codes
  const referralCodesFeatureFlag = useFeatureFlag('referral_codes_enabled');
  // GW-5444: Check feature flag for Personal Missions entry
  const missionsMarketplaceFeatureFlag = useFeatureFlag('missions_marketplace');
  // GW-8662: Check feature flag for Applications marketplace entry
  const applicationsMarketplaceFeatureFlag = useFeatureFlag('applications_marketplace');
  // GW-8665: Check feature flag for Services entry
  const servicesMarketplaceFeatureFlag = useFeatureFlag('services_marketplace');

  // Create a stable map of feature flag states for filtering (GW-4687: memoized to avoid re-render cascade)
  const featureFlagStates = useMemo<Record<string, boolean>>(
    () => ({
      datasets_marketplace: datasetsFeatureFlag.isEnabled,
      gift_cards_enabled: giftCardsFeatureFlag.isEnabled,
      referral_codes_enabled: referralCodesFeatureFlag.isEnabled,
      missions_marketplace: missionsMarketplaceFeatureFlag.isEnabled,
      applications_marketplace: applicationsMarketplaceFeatureFlag.isEnabled,
      services_marketplace: servicesMarketplaceFeatureFlag.isEnabled,
    }),
    [
      datasetsFeatureFlag.isEnabled,
      giftCardsFeatureFlag.isEnabled,
      referralCodesFeatureFlag.isEnabled,
      missionsMarketplaceFeatureFlag.isEnabled,
      applicationsMarketplaceFeatureFlag.isEnabled,
      servicesMarketplaceFeatureFlag.isEnabled,
    ]
  );

  // GW-4687: Memoize navigation filtering to prevent re-renders from cascading
  // store updates (auth, org, feature flags) causing layout jitter.
  const orgRoles = currentOrg?.roles;

  // Filter organization navigation items based on user role and feature flags
  // GW-776: global_admin sees all organization navigation items
  // GW-1578: Organization items may also require feature flags
  const visibleOrganizationItems = useMemo(
    () =>
      organizationNavigationItems.filter((item) => {
        if (item.requiredFeatureFlag) {
          if (!(featureFlagStates[item.requiredFeatureFlag] ?? false)) {
            return false;
          }
        }
        if (isGlobalAdmin && currentOrg) {
          return true;
        }
        if (item.requiredRoles) {
          return orgRoles?.some((role) => item.requiredRoles!.includes(role));
        }
        if (!item.requiredRole) {
          return true;
        }
        return orgRoles?.some((role) => role === item.requiredRole);
      }),
    [featureFlagStates, isGlobalAdmin, currentOrg, orgRoles]
  );

  // Filter developer navigation items based on user role and feature flags
  // GW-776: global_admin sees all developer navigation items
  // GW-1416: Developer items may also require feature flags
  const visibleDeveloperItems = useMemo(
    () =>
      developerNavigationItems.filter((item) => {
        if (item.requiredFeatureFlag) {
          if (!(featureFlagStates[item.requiredFeatureFlag] ?? false)) {
            return false;
          }
        }
        if (isGlobalAdmin) {
          return true;
        }
        if (item.requiredRoles) {
          return orgRoles?.some((role) => item.requiredRoles!.includes(role));
        }
        if (!item.requiredRole) {
          return true;
        }
        return orgRoles?.some((role) => role === item.requiredRole);
      }),
    [featureFlagStates, isGlobalAdmin, orgRoles]
  );

  return (
    <TooltipProvider delayDuration={0}>
      {/* Mobile overlay — GW-9546: Always render with transition-opacity (root cause #3: fade in/out, not hard-cut) */}
      <div
        className={cn(
          'fixed inset-0 z-40 bg-black/50 transition-opacity duration-200 lg:hidden',
          isOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
        )}
        onClick={() => {
          setIsTransitioning(true);
          onClose();
        }}
      />

      {/* Sidebar */}
      <aside
        role={isOpen ? 'dialog' : undefined}
        aria-modal={isOpen ? true : undefined}
        aria-label="Navigation menu"
        data-testid="layout-sidebar"
        data-open={isOpen}
        data-collapsed={isCollapsed}
        onTransitionEnd={() => setIsTransitioning(false)}
        className={cn(
          'bg-card fixed inset-y-0 left-0 z-50 transform border-r lg:relative lg:translate-x-0',
          isTransitioning && 'transition-[transform,width] duration-200 ease-in-out',
          isOpen ? 'translate-x-0' : '-translate-x-full',
          isCollapsed ? 'w-16' : 'w-64'
        )}
      >
        <div className="flex h-full flex-col">
          {/* Logo and close/collapse buttons */}
          <div className="flex h-16 items-center justify-between border-b px-3">
            {/* Mobile close button */}
            <Button
              variant="ghost"
              size="icon"
              onClick={() => {
                setIsTransitioning(true);
                onClose();
              }}
              className="min-h-[44px] min-w-[44px] lg:hidden"
              aria-label="Close menu"
            >
              <X className="h-5 w-5" />
            </Button>

            {/* Desktop collapse button */}
            <Button
              variant="ghost"
              size="icon"
              onClick={() => {
                setIsTransitioning(true);
                onToggleCollapse();
              }}
              className={cn(
                'relative hidden lg:flex',
                isCollapsed ? 'mx-auto' : 'ml-auto',
                showGlow && 'animate-glow-fade'
              )}
              aria-label={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
              aria-expanded={!isCollapsed}
            >
              {isCollapsed ? (
                <ChevronRight className="h-5 w-5" />
              ) : (
                <ChevronLeft className="h-5 w-5" />
              )}
            </Button>
          </div>

          {/* Navigation items - independently scrollable */}
          <nav className="flex-1 space-y-1 overflow-y-auto px-3 py-4">
            {/* GW-2149: Marketplace section - header navigates to landing page */}
            {/* GW-8104: Home — standalone, ungrouped, top of sidebar (target IA §1.2) */}
            {(() => {
              const isActive = location.pathname === homeNavigationItem.href;
              const Icon = homeNavigationItem.icon;
              return (
                <SidebarTooltip label={homeNavigationItem.title} isCollapsed={isCollapsed}>
                  <PrefetchLink
                    to={homeNavigationItem.href}
                    onClick={() => {
                      if (window.innerWidth < 1024) {
                        onClose();
                      }
                    }}
                    className={cn(
                      'flex items-center px-3 py-2 text-sm font-medium transition-colors duration-200',
                      isCollapsed ? 'justify-center' : 'gap-3',
                      isActive
                        ? 'bg-primary text-primary-foreground'
                        : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground'
                    )}
                  >
                    <Icon className="h-5 w-5 shrink-0" />
                    {!isCollapsed && <span>{homeNavigationItem.title}</span>}
                  </PrefetchLink>
                </SidebarTooltip>
              );
            })()}

            {/* GW-8104: Marketplace group (target IA §1.2) */}
            <Separator className="my-2" />
            <SectionHeader label="Marketplace" isCollapsed={isCollapsed} />
            {marketplaceNavigationItems
              .filter((item) => {
                if (!item.requiredFeatureFlag) return true;
                return featureFlagStates[item.requiredFeatureFlag] ?? false;
              })
              .map((item) => {
                // GW-12209: prefix/section match so catalog detail pages
                // (/marketplace/{type}/{slug}) keep their Marketplace item active.
                const isActive = isSectionActive(location.pathname, item.href);
                const Icon = item.icon;

                return (
                  <SidebarTooltip key={item.href} label={item.title} isCollapsed={isCollapsed}>
                    <PrefetchLink
                      to={item.href}
                      onClick={() => {
                        if (window.innerWidth < 1024) {
                          onClose();
                        }
                      }}
                      className={cn(
                        'flex items-center px-3 py-2 text-sm font-medium transition-colors duration-200',
                        isCollapsed ? 'justify-center' : 'gap-3',
                        isActive
                          ? 'bg-primary text-primary-foreground'
                          : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground'
                      )}
                    >
                      <Icon className="h-5 w-5 shrink-0" />
                      {!isCollapsed && <span>{item.title}</span>}
                    </PrefetchLink>
                  </SidebarTooltip>
                );
              })}

            {/* GW-8104: Workspace group (target IA §1.2) — single "My Work" item.
                GW-12256: links to the /personal section root; the flag-aware
                default-tab choice lives in PersonalIndexRedirect. */}
            <Separator className="my-2" />
            <SectionHeader label="Workspace" isCollapsed={isCollapsed} />
            {(() => {
              // GW-12209: match the whole /personal section, not just the
              // default tab, so every My Work tab keeps the item highlighted.
              const isActive = isSectionActive(location.pathname, WORKSPACE_SECTION);
              const Icon = WORKSPACE_MY_WORK_ITEM.icon;

              return (
                <SidebarTooltip label={WORKSPACE_MY_WORK_ITEM.title} isCollapsed={isCollapsed}>
                  <PrefetchLink
                    to={WORKSPACE_MY_WORK_ITEM.href}
                    onClick={() => {
                      if (window.innerWidth < 1024) {
                        onClose();
                      }
                    }}
                    className={cn(
                      'flex items-center px-3 py-2 text-sm font-medium transition-colors duration-200',
                      isCollapsed ? 'justify-center' : 'gap-3',
                      isActive
                        ? 'bg-primary text-primary-foreground'
                        : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground'
                    )}
                    data-testid="sidebar-nav-workspace-my-work"
                  >
                    <Icon className="h-5 w-5 shrink-0" />
                    {!isCollapsed && <span>{WORKSPACE_MY_WORK_ITEM.title}</span>}
                  </PrefetchLink>
                </SidebarTooltip>
              );
            })()}

            {/* GW-8104: Admin group (target IA §1.2) — merges the former
                  Organization (org_admin-gated) and Developer/Publishing
                  (publisher-role-gated) sections under one "Admin" label.
                  Each item keeps its exact original role/feature-flag gate —
                  this is a structural regroup, not a permissions change. The
                  wireframe (00-search-entry.html) shows "Organization" and
                  "Publishing" as two flat links under one "Admin" group-label
                  div, with no further sub-grouping markup, so items are
                  rendered as a single flat list (org items first, then
                  publisher items) rather than two visually separated
                  sub-groups. */}
            {(visibleOrganizationItems.length > 0 || visibleDeveloperItems.length > 0) && (
              <>
                <Separator className="my-2" />
                <SectionHeader label="Admin" isCollapsed={isCollapsed} />
                {visibleOrganizationItems.map((item) => {
                  const isActive = location.pathname.startsWith('/organization');
                  const Icon = item.icon;
                  // GW-2351: Show badge on the Organization link when the
                  // publisher agreement (surfaced as the Terms of Service tab
                  // inside OrganizationWorkspacePage) needs re-acceptance.
                  const showTOSBadge = publisherAgreementNeedsReacceptance;

                  return (
                    <SidebarTooltip
                      key={item.href}
                      label={item.title}
                      description={showTOSBadge ? '1 TOS to Review' : undefined}
                      isCollapsed={isCollapsed}
                    >
                      <PrefetchLink
                        to={item.href}
                        onClick={() => {
                          if (window.innerWidth < 1024) {
                            onClose();
                          }
                        }}
                        className={cn(
                          'flex items-center px-3 py-2 text-sm font-medium transition-colors duration-200',
                          isCollapsed ? 'justify-center' : 'gap-3',
                          isActive
                            ? 'bg-primary text-primary-foreground'
                            : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground'
                        )}
                      >
                        {isCollapsed && showTOSBadge ? (
                          <span className="relative">
                            <Icon className="h-5 w-5 shrink-0" />
                            <Badge
                              variant="destructive"
                              className="absolute -top-1 -right-1 h-4 min-w-4 p-0 text-[10px]"
                              data-testid="publisher-agreement-badge-collapsed"
                            >
                              1
                            </Badge>
                          </span>
                        ) : (
                          <Icon className="h-5 w-5 shrink-0" />
                        )}
                        {!isCollapsed && (
                          <>
                            <span>{item.title}</span>
                            {showTOSBadge && (
                              <Badge
                                variant={isActive ? 'secondary' : 'default'}
                                className="ml-auto"
                                data-testid="publisher-agreement-badge"
                              >
                                1
                              </Badge>
                            )}
                          </>
                        )}
                      </PrefetchLink>
                    </SidebarTooltip>
                  );
                })}
                {visibleDeveloperItems.map((item) => {
                  const isActive =
                    location.pathname.startsWith('/publisher') ||
                    location.pathname.startsWith('/developer');
                  const Icon = item.icon;

                  return (
                    <SidebarTooltip key={item.href} label={item.title} isCollapsed={isCollapsed}>
                      <PrefetchLink
                        to={item.href}
                        onClick={() => {
                          if (window.innerWidth < 1024) {
                            onClose();
                          }
                        }}
                        className={cn(
                          'flex items-center px-3 py-2 text-sm font-medium transition-colors duration-200',
                          isCollapsed ? 'justify-center' : 'gap-3',
                          isActive
                            ? 'bg-primary text-primary-foreground'
                            : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground'
                        )}
                      >
                        <Icon className="h-5 w-5 shrink-0" />
                        {!isCollapsed && <span>{item.title}</span>}
                      </PrefetchLink>
                    </SidebarTooltip>
                  );
                })}
              </>
            )}

            {/* GW-8104: Settings — standalone, ungrouped, bottom of sidebar
                  (target IA §1.2). GW-430's "Help" link moved out of the
                  sidebar entirely — the Header's UserMenu (HeaderHost.tsx)
                  already surfaces an identical external Help link, and the
                  target IA wireframe has no Support/Help section in the
                  sidebar at all. */}
            <Separator className="my-2" />
            {(() => {
              // GW-12209: prefix/section match so Settings tab sub-routes
              // (/settings/security, etc.) keep Settings highlighted.
              const isActive = isSectionActive(location.pathname, settingsNavigationItem.href);
              const Icon = settingsNavigationItem.icon;
              return (
                <SidebarTooltip label={settingsNavigationItem.title} isCollapsed={isCollapsed}>
                  <PrefetchLink
                    to={settingsNavigationItem.href}
                    onClick={() => {
                      if (window.innerWidth < 1024) {
                        onClose();
                      }
                    }}
                    className={cn(
                      'flex items-center px-3 py-2 text-sm font-medium transition-colors duration-200',
                      isCollapsed ? 'justify-center' : 'gap-3',
                      isActive
                        ? 'bg-primary text-primary-foreground'
                        : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground'
                    )}
                  >
                    <Icon className="h-5 w-5 shrink-0" />
                    {!isCollapsed && <span>{settingsNavigationItem.title}</span>}
                  </PrefetchLink>
                </SidebarTooltip>
              );
            })()}
          </nav>
        </div>
      </aside>
    </TooltipProvider>
  );
}
